import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { formatINR } from "../lib/utils-format";
import { Search, Copy, CheckCircle2, Plane, Clock, GitMerge, Building2, User } from "lucide-react";
import { toast } from "sonner";

const TYPE_BADGE = {
    direct_bonus_credit:     { label: "Direct Referral",      cls: "bg-[#10B1B6]/10 text-[#10B1B6]"   },
    direct_bonus_deduction:  { label: "Business Cut",         cls: "bg-[#E45B80]/10 text-[#E45B80]"   },
    pairing_bonus_credit:    { label: "Pairing Bonus",        cls: "bg-[#7059FD]/10 text-[#7059FD]"   },
    pairing_bonus_deduction: { label: "Business Cut",         cls: "bg-[#E45B80]/10 text-[#E45B80]"   },
    admin_charge_deduction:  { label: "Admin Charges (15%)",  cls: "bg-orange-100 text-orange-700"     },
    tds_deduction:           { label: "TDS (5%)",             cls: "bg-red-100 text-red-700"           },
    travel_fund_credit:      { label: "Travel Fund (5%)",     cls: "bg-blue-100 text-blue-700"         },
    travel_fund_debit:       { label: "Fund Claim",           cls: "bg-amber-100 text-amber-700"       },
    withdrawal_debit:        { label: "Withdrawal",           cls: "bg-amber-100 text-amber-700"       },
    withdrawal_refund:       { label: "Refund",               cls: "bg-blue-100 text-blue-700"         },
    adjustment:              { label: "Adjustment",           cls: "bg-slate-100 text-slate-600"       },
};

const BONUS_TYPES = new Set(["pairing_bonus_credit", "direct_bonus_credit"]);

/* Compute breakdown amounts for a ledger entry.
   Uses real meta when available; falls back to derived 25% split for legacy entries.
   For pairing runs that include biz pairs, gross/deductions are scoped to the member's
   portion only so the table row reconciles: gross − admin − tds − fund = net. */
function getBreakdown(entry) {
    if (!BONUS_TYPES.has(entry.type)) return null;
    const m = entry.meta;
    if (m?.admin_charge != null) {
        const memberPairs = m.member_pairs ?? null;
        const totalPairs  = (m.member_pairs ?? 0) + (m.biz_pairs ?? 0);
        // Scale deductions to member pairs only when there's a biz split
        const scale = (memberPairs != null && totalPairs > 0 && memberPairs < totalPairs)
            ? memberPairs / totalPairs
            : 1;
        return {
            gross:  m.gross  * scale,
            admin:  m.admin_charge * scale,
            tds:    m.tds    * scale,
            fund:   m.travel_fund  * scale,
            bizNet: scale < 1 ? m.gross * (1 - scale) * 0.75 : 0,  // portion of net that went to biz
            legacy: false,
        };
    }
    // Legacy entry — derive from net (net = 75% of gross)
    const gross = entry.amount / 0.75;
    return { gross, admin: gross * 0.15, tds: gross * 0.05, fund: gross * 0.05, bizNet: 0, legacy: true };
}

/* Rule 2: derive which pair numbers were yours vs business from meta */
function getPairNumbers(meta) {
    if (!meta || meta.pairs == null || meta.start_pair == null) return null;
    const yours = [], biz = [];
    for (let i = 0; i < meta.pairs; i++) {
        const n = meta.start_pair + i;
        // Pairs 1,2 → member; odd 3-25 → biz; even or >25 → member
        const isBiz = n >= 3 && n <= 25 && n % 2 === 1;
        (isBiz ? biz : yours).push(n);
    }
    return { yours, biz };
}

function PairChip({ num, dest }) {
    return dest === "biz"
        ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Building2 className="w-2 h-2" />#{num}
          </span>
        : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <User className="w-2 h-2" />#{num}
          </span>;
}

function PairingHistory({ entries }) {
    if (!entries.length) return null;
    const runs = entries.filter(e => e.type === "pairing_bonus_credit" && e.meta);
    if (!runs.length) return null;

    return (
        <div className="bg-white rounded-3xl shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center">
                    <GitMerge className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                    <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Audit Trail</span>
                    <h3 className="text-lg font-extrabold text-slate-900 leading-tight">Pairing Run History</h3>
                </div>
            </div>
            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
                Every pair is numbered cumulatively. Pairs&nbsp;#1&nbsp;&amp;&nbsp;#2 always go to your wallet.
                From pair&nbsp;#3 to&nbsp;#25, every <span className="font-bold text-amber-600">odd pair</span> (3, 5, 7…) goes to the business wallet.
                Every <span className="font-bold text-emerald-600">even pair</span> and all pairs above&nbsp;#25 come to you.
            </p>

            <div className="space-y-4">
                {[...runs].reverse().map((entry, i) => {
                    const m = entry.meta;
                    const pn = getPairNumbers(m);
                    const endPair = m.start_pair + m.pairs - 1;
                    const bizEarned = (m.biz_pairs ?? 0) * (m.net_per_pair ?? 0);
                    const runDate = new Date(entry.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

                    return (
                        <div key={entry.id} className={`rounded-2xl border p-4 ${m.biz_pairs > 0 ? "border-amber-200 bg-amber-50/30" : "border-emerald-100 bg-emerald-50/20"}`}>
                            {/* Run header */}
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-500">{runDate}</span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                        Pairs #{m.start_pair}–#{endPair} &nbsp;·&nbsp; {m.pairs} total
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs font-bold">
                                    <span className="text-emerald-600">+{formatINR(entry.amount)} to you</span>
                                    {m.biz_pairs > 0 && (
                                        <span className="text-amber-600">{formatINR(bizEarned)} to business</span>
                                    )}
                                </div>
                            </div>

                            {/* Pair chips */}
                            {pn && (
                                <div className="space-y-2">
                                    {pn.yours.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <span className="text-[9px] font-extrabold uppercase tracking-widest text-emerald-600 w-20 shrink-0">Your pairs</span>
                                            {pn.yours.map(n => <PairChip key={n} num={n} dest="yours" />)}
                                            <span className="text-[9px] text-slate-400 ml-1">{pn.yours.length} pair{pn.yours.length !== 1 ? "s" : ""} × {formatINR(m.net_per_pair)} = {formatINR(entry.amount)}</span>
                                        </div>
                                    )}
                                    {pn.biz.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <span className="text-[9px] font-extrabold uppercase tracking-widest text-amber-600 w-20 shrink-0">Business</span>
                                            {pn.biz.map(n => <PairChip key={n} num={n} dest="biz" />)}
                                            <span className="text-[9px] text-slate-400 ml-1">{pn.biz.length} odd pair{pn.biz.length !== 1 ? "s" : ""} (Rule 2) × {formatINR(m.net_per_pair)} = {formatINR(bizEarned)}</span>
                                        </div>
                                    )}
                                    {pn.biz.length === 0 && (
                                        <p className="text-[10px] text-emerald-600 font-semibold">All pairs credited to your wallet — no business split this run.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function WalletPage() {
    const { user } = useAuth();
    const [balance, setBalance]           = useState(null);
    const [ledger,  setLedger]            = useState([]);
    const [search,  setSearch]            = useState("");
    const [inviteCopied, setInviteCopied] = useState(false);

    const leftCount   = user?.left_active_count  ?? 0;
    const rightCount  = user?.right_active_count ?? 0;
    const symmetryPct = Math.max(leftCount, rightCount) > 0
        ? Math.round((Math.min(leftCount, rightCount) / Math.max(leftCount, rightCount)) * 100)
        : 0;

    useEffect(() => {
        Promise.all([
            api.get("/wallet/balance"),
            api.get("/wallet/ledger", { params: { limit: 200 } }),
        ]).then(([b, l]) => {
            setBalance(b.data);
            setLedger(l.data);
        }).catch(() => {});
    }, []);

    const bonusLedger = ledger.filter(l => BONUS_TYPES.has(l.type));
    const filtered = search.trim()
        ? bonusLedger.filter(l =>
            l.description?.toLowerCase().includes(search.toLowerCase()) ||
            (TYPE_BADGE[l.type]?.label ?? l.type).toLowerCase().includes(search.toLowerCase())
          )
        : bonusLedger;

    const copyInviteLink = () => {
        if (!user?.referral_code) return;
        navigator.clipboard.writeText(user.referral_code).catch(() => {});
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 2000);
    };

    const tfBalance  = balance?.travel_fund_balance ?? 0;
    const tfPending  = balance?.travel_fund_pending  ?? 0;
    const tfTotal    = tfBalance + tfPending;
    const tfClaimable = tfBalance >= 500;

    return (
        <AppShell>
            <div className="flex gap-6 items-start">

                {/* ─────────────── Main column ─────────────── */}
                <div className="flex-1 min-w-0 space-y-6 pb-12">

                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="overline">Wallet</div>
                            <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                                Your Earnings Ledger
                            </h1>
                        </div>
                        <div className="relative w-48 sm:w-60">
                            <input
                                type="text"
                                placeholder="Search transactions..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-white rounded-full text-xs shadow-sm
                                    border border-slate-100 outline-none focus:ring-2 focus:ring-[#2172E5] transition-all"
                            />
                            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                        </div>
                    </div>

                    {/* ── Wallet balance card + Travel Fund card ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

                        {/* Main wallet balance card */}
                        <div
                            className="lg:col-span-5 bg-gradient-to-br from-[#0c2b5c] via-[#103b7b] to-[#2172e5]
                                text-white p-6 rounded-3xl shadow-lg relative overflow-hidden flex flex-col
                                justify-between h-52 hover:scale-[1.01] transition-transform"
                            data-testid="wallet-balance"
                        >
                            <div className="flex justify-between items-start z-10">
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase">
                                        Available Balance
                                    </span>
                                    <h2 className="text-4xl font-extrabold tracking-tight text-white">
                                        {formatINR(balance?.available_balance ?? 0)}
                                    </h2>
                                    {(balance?.pending_balance ?? 0) > 0 && (
                                        <p className="text-[11px] text-amber-300 font-medium flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatINR(balance.pending_balance)} pending (hold period)
                                        </p>
                                    )}
                                </div>
                                <div className="px-2.5 py-1 bg-white/10 rounded-xl border border-white/20 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    <span className="text-[9px] font-black tracking-wider uppercase text-white/90">Secure</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-4 z-10">
                                <div>
                                    <span className="text-[9px] font-bold text-white/50 tracking-wider block">Available + Pending</span>
                                    <span className="text-lg font-extrabold text-emerald-400">
                                        {formatINR((balance?.available_balance ?? 0) + (balance?.pending_balance ?? 0))}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-[9px] font-bold text-white/50 tracking-wider block">Total Withdrawn</span>
                                    <span className="text-lg font-extrabold text-slate-300">
                                        {formatINR(balance?.total_debits ?? 0)}
                                    </span>
                                </div>
                            </div>
                            <div className="absolute right-0 bottom-0 translate-x-8 translate-y-8 w-32 h-32
                                bg-[#10B1B6] rounded-full blur-2xl opacity-20 pointer-events-none" />
                        </div>

                        {/* Travel Fund card */}
                        <div className="lg:col-span-3 bg-white rounded-3xl shadow-sm p-5 flex flex-col justify-between h-52 border border-blue-100">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                                        <Plane className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Travel Fund</span>
                                </div>
                                <div className="text-3xl font-extrabold text-blue-600 tracking-tight">
                                    {formatINR(tfBalance)}
                                </div>
                                <div className="flex gap-3 mt-1.5 text-[10px] font-semibold">
                                    {tfPending > 0 && (
                                        <span className="text-amber-500 flex items-center gap-0.5">
                                            <Clock className="w-2.5 h-2.5" />
                                            {formatINR(tfPending)} on hold (3-day settlement)
                                        </span>
                                    )}
                                    {tfTotal > 0 && (
                                        <span className="text-slate-400">
                                            total {formatINR(tfTotal)}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                                    5% of every bonus is credited here monthly.
                                    Claimable when balance ≥ ₹500.
                                </p>
                            </div>
                            <div>
                                {tfClaimable ? (
                                    <button
                                        onClick={() => toast.info("Travel fund claim feature coming soon.")}
                                        className="w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-700
                                            text-white text-xs font-bold transition-all"
                                    >
                                        Claim ₹{Math.floor(tfBalance)} →
                                    </button>
                                ) : (
                                    <div className="w-full">
                                        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                            <span>Progress to claim</span>
                                            <span>{formatINR(tfTotal)} / ₹500</span>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-400 rounded-full transition-all duration-500"
                                                style={{ width: `${Math.min(100, (tfTotal / 500) * 100)}%` }}
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            ₹{Math.max(0, 500 - tfTotal).toFixed(2)} more needed to claim
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Info card */}
                        <div className="lg:col-span-4 bg-white border-l-8 border-[#10B1B6] p-5 rounded-3xl shadow-sm
                            flex flex-col justify-between h-52">
                            <div className="space-y-2">
                                <span className="text-xs font-bold text-[#10B1B6] tracking-widest uppercase block">
                                    Deduction Breakdown
                                </span>
                                <h3 className="text-base font-extrabold text-slate-900 leading-tight">
                                    25% deducted from every bonus
                                </h3>
                                <div className="space-y-1.5 text-xs">
                                    <div className="flex items-center justify-between">
                                        <span className="text-orange-600 font-semibold">Admin Charges</span>
                                        <span className="font-bold text-slate-700">15%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-500 font-semibold">TDS (Tax Deducted)</span>
                                        <span className="font-bold text-slate-700">5%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-blue-500 font-semibold">Travel Fund</span>
                                        <span className="font-bold text-slate-700">5%</span>
                                    </div>
                                    <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
                                        <span className="text-slate-600 font-bold">You receive (net)</span>
                                        <span className="font-extrabold text-[#10B1B6]">75%</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={copyInviteLink}
                                className="flex items-center justify-center gap-1.5 py-2 bg-slate-50 hover:bg-slate-100
                                    text-xs font-bold rounded-xl transition-all text-slate-600 border border-slate-100"
                            >
                                {inviteCopied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                {inviteCopied ? "Copied!" : "Share Sponsor Link"}
                            </button>
                        </div>
                    </div>

                    {/* ── Transactions table ── */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm" data-testid="ledger-table">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Ledger Data</span>
                                <h3 className="text-lg font-extrabold text-slate-900 mt-0.5">Transactions</h3>
                            </div>
                            <span className="px-2.5 py-1 bg-[#10B1B6]/10 text-[#10B1B6] text-xs font-bold rounded-lg">
                                {filtered.length} {filtered.length === 1 ? "Record" : "Records"} found
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        <th className="pb-3 pr-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">When</th>
                                        <th className="pb-3 pr-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">Type</th>
                                        <th className="pb-3 pr-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Description</th>
                                        <th className="pb-3 pr-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Gross</th>
                                        <th className="pb-3 pr-3 text-[10px] font-extrabold text-orange-400 uppercase tracking-widest text-right whitespace-nowrap">Admin (15%)</th>
                                        <th className="pb-3 pr-3 text-[10px] font-extrabold text-red-400 uppercase tracking-widest text-right whitespace-nowrap">TDS (5%)</th>
                                        <th className="pb-3 pr-3 text-[10px] font-extrabold text-blue-400 uppercase tracking-widest text-right whitespace-nowrap">Travel Fund (5%)</th>
                                        <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Net Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="text-center py-10 text-slate-400">
                                                No transactions yet.
                                            </td>
                                        </tr>
                                    )}
                                    {filtered.map((l, i) => {
                                        const badge = TYPE_BADGE[l.type] ?? { label: l.type, cls: "bg-slate-100 text-slate-600" };
                                        const bk    = getBreakdown(l);
                                        const evenBg = i % 2 === 0 ? "" : "bg-slate-50/40";

                                        return (
                                            <tr
                                                key={l.id}
                                                className={`${evenBg} hover:bg-blue-50/20 transition-colors`}
                                                data-testid={`ledger-row-${l.id}`}
                                            >
                                                {/* When */}
                                                <td className="py-3.5 pr-4 text-slate-500 whitespace-nowrap font-semibold align-middle">
                                                    {new Date(l.created_at).toLocaleString("en-IN")}
                                                </td>

                                                {/* Type badge */}
                                                <td className="py-3.5 pr-4 align-middle">
                                                    <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-bold rounded-lg uppercase whitespace-nowrap ${badge.cls}`}>
                                                        {badge.label}
                                                    </span>
                                                    {l.type === "pairing_bonus_credit" && l.meta?.biz_pairs > 0 && (
                                                        <span className="mt-1 flex items-center gap-1 text-[9px] font-semibold text-amber-600">
                                                            <Building2 className="w-2.5 h-2.5" />
                                                            {l.meta.member_pairs}/{l.meta.pairs} pairs yours · {l.meta.biz_pairs} to biz
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Description */}
                                                <td className="py-3.5 pr-4 text-slate-700 max-w-xs align-middle">
                                                    {l.description}
                                                    {bk?.legacy && (
                                                        <span className="ml-1 text-[9px] text-slate-400">(est.)</span>
                                                    )}
                                                </td>

                                                {/* Gross */}
                                                <td className="py-3.5 pr-3 text-right font-mono text-slate-600 whitespace-nowrap align-middle">
                                                    {bk ? formatINR(bk.gross) : <span className="text-slate-200">—</span>}
                                                </td>

                                                {/* Admin charges */}
                                                <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle">
                                                    {bk
                                                        ? <span className="text-orange-600 font-semibold">−{formatINR(bk.admin)}</span>
                                                        : <span className="text-slate-200">—</span>
                                                    }
                                                </td>

                                                {/* TDS */}
                                                <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle">
                                                    {bk
                                                        ? <span className="text-red-500 font-semibold">−{formatINR(bk.tds)}</span>
                                                        : <span className="text-slate-200">—</span>
                                                    }
                                                </td>

                                                {/* Travel fund */}
                                                <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle">
                                                    {bk
                                                        ? <span className="text-blue-500 font-semibold">+{formatINR(bk.fund)}</span>
                                                        : <span className="text-slate-200">—</span>
                                                    }
                                                </td>

                                                {/* Net amount */}
                                                <td className={`py-3.5 text-right font-extrabold text-sm whitespace-nowrap align-middle
                                                    ${l.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                                    {l.amount >= 0 ? "+" : ""}{formatINR(l.amount)}
                                                    {bk?.bizNet > 0 && (
                                                        <span className="block text-[9px] font-semibold text-amber-600 mt-0.5">
                                                            +{formatINR(bk.bizNet)} → biz
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {filtered.length > 0 && (() => {
                                    const bonusRows = filtered.filter(l => BONUS_TYPES.has(l.type));
                                    if (!bonusRows.length) return null;
                                    const totals = bonusRows.reduce((acc, l) => {
                                        const bk = getBreakdown(l);
                                        if (bk) {
                                            acc.gross += bk.gross;
                                            acc.admin += bk.admin;
                                            acc.tds   += bk.tds;
                                            acc.fund  += bk.fund;
                                        }
                                        acc.net += l.amount;
                                        return acc;
                                    }, { gross: 0, admin: 0, tds: 0, fund: 0, net: 0 });
                                    return (
                                        <tfoot>
                                            <tr className="border-t-2 border-slate-100 bg-slate-50/80">
                                                <td colSpan={3} className="py-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                                                    Bonus totals ({bonusRows.length} entries)
                                                </td>
                                                <td className="py-3 pr-3 text-right text-xs font-mono font-bold text-slate-600">{formatINR(totals.gross)}</td>
                                                <td className="py-3 pr-3 text-right text-xs font-mono font-bold text-orange-600">−{formatINR(totals.admin)}</td>
                                                <td className="py-3 pr-3 text-right text-xs font-mono font-bold text-red-500">−{formatINR(totals.tds)}</td>
                                                <td className="py-3 pr-3 text-right text-xs font-mono font-bold text-blue-500">
                                                    +{formatINR(tfTotal)}
                                                    <span className="block text-[9px] text-slate-400 font-normal">actual credited</span>
                                                </td>
                                                <td className="py-3 text-right text-xs font-mono font-extrabold text-emerald-600">+{formatINR(totals.net)}</td>
                                            </tr>
                                        </tfoot>
                                    );
                                })()}
                            </table>
                        </div>
                    </div>
                    {/* ── Pairing Run History ── */}
                    <PairingHistory entries={bonusLedger} />

                </div>

                {/* ─────────────── Right sidebar ─────────────── */}
                <div className="w-64 hidden xl:flex flex-col flex-shrink-0 bg-white rounded-3xl shadow-sm p-6
                    self-stretch justify-between" style={{ minHeight: "520px" }}>
                    <div className="space-y-5">
                        <div>
                            <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Wing Ratio Status</span>
                            <h3 className="text-lg font-bold text-slate-900 mt-1">Network Symmetry</h3>
                        </div>

                        {/* Symmetry progress */}
                        <div className="p-4 bg-[#EDF2F9] rounded-2xl border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-500">Wing symmetry</span>
                                <span className="text-xs font-bold text-[#2172E5]">{symmetryPct}%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                                <div
                                    className="bg-[#2172E5] h-full rounded-full transition-all duration-500"
                                    style={{ width: `${symmetryPct}%` }}
                                />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2">
                                {Math.min(leftCount, rightCount)} matched of {Math.max(leftCount, rightCount)} potential pairs
                            </p>
                        </div>

                        {/* Travel fund mini-card */}
                        <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Plane className="w-3.5 h-3.5 text-blue-500" />
                                <span className="text-[10px] font-extrabold text-blue-600 uppercase tracking-widest">Travel Fund</span>
                            </div>
                            <div className="text-2xl font-extrabold text-blue-700">{formatINR(tfBalance)}</div>
                            {tfPending > 0 && (
                                <p className="text-[10px] text-amber-500 mt-0.5">+{formatINR(tfPending)} pending</p>
                            )}
                            <div className="mt-2">
                                <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all"
                                        style={{ width: `${Math.min(100, (tfTotal / 500) * 100)}%` }}
                                    />
                                </div>
                                <p className="text-[10px] text-blue-400 mt-1">
                                    {tfClaimable ? "✓ Ready to claim!" : `₹${Math.max(0, 500 - tfTotal).toFixed(0)} to go`}
                                </p>
                            </div>
                        </div>

                        {/* Deduction breakdown legend */}
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Deduction Split</h4>
                            <div className="space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-orange-600 font-medium">Admin Charges</span>
                                    <span className="font-bold text-slate-600">15%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-red-500 font-medium">TDS</span>
                                    <span className="font-bold text-slate-600">5%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-blue-500 font-medium">Travel Fund</span>
                                    <span className="font-bold text-slate-600">5%</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-100 pt-1.5">
                                    <span className="text-[#10B1B6] font-bold">Your net share</span>
                                    <span className="font-extrabold text-[#10B1B6]">75%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Copy invite link */}
                    <div className="pt-4 border-t border-slate-100">
                        <button
                            onClick={copyInviteLink}
                            className="w-full py-3 bg-slate-50 hover:bg-slate-100 text-xs font-bold rounded-2xl
                                transition-all border border-slate-100 flex items-center justify-center gap-2 text-slate-600"
                        >
                            {inviteCopied
                                ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                : <Copy className="w-4 h-4 text-slate-400" />
                            }
                            {inviteCopied ? "Copied!" : "+ Copy Partner Invite Link"}
                        </button>
                    </div>
                </div>

            </div>
        </AppShell>
    );
}
